import React, { useMemo } from 'react';
import { Select, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { IMPORT_COUNTRY_OPTIONS } from '../../AddUserListing/constants/listingOptions';
import { MdSearch } from 'react-icons/md';
import { POPULAR_MAKES } from '../mysearchConstants';
import { DEFAULT_CAR_IMAGE } from '../../components/SelectWithImages';

const buildGroupedMakeOptions = (opts: { value: string; label: string; image?: string }[]) => {
  const popularOpts = opts.filter(o =>
    POPULAR_MAKES.some(pm => pm.toLowerCase() === o.label.toLowerCase())
  );
  if (!popularOpts.length) return opts;
  return [
    { label: 'Popular Makes', options: popularOpts },
    { label: 'All Makes', options: opts },
  ];
};

interface QsbState {
  searchQuery: string;
  make: string;
  model: string;
  isImported?: boolean;
  importCountry?: string;
}

interface QuickSearchBarProps {
  qsb: QsbState;
  makeOptions: { value: string; label: string; image?: string }[];
  modelOptions: { value: string; label: string }[];
  loadingModels: boolean;
  onFilterChange: (key: keyof QsbState, value: string | boolean) => void;
  onMakeChange: (value: string) => void;
  onSearch: () => void;
}

const QuickSearchBar: React.FC<QuickSearchBarProps> = ({
  qsb,
  makeOptions,
  modelOptions,
  loadingModels,
  onFilterChange,
  onMakeChange,
  onSearch,
}) => {
  const { t } = useTranslation();
  const groupedMakeOptions = useMemo(() => buildGroupedMakeOptions(makeOptions), [makeOptions]);
  return (
  <div className="qsb-root">
    <div className="qsb-inner">
      <div
        role="tablist"
        aria-label="Vehicle source"
        className="qsb-tabs"
        style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgba(0,0,0,0.1)', marginBottom: 12 }}
      >
        {[
          { value: false, label: t('quick_search.all_vehicles') },
          { value: true, label: t('quick_search.imported_vehicles') },
        ].map((opt) => {
          const active = !!qsb.isImported === opt.value;
          return (
            <button
              key={String(opt.value)}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onFilterChange('isImported', opt.value)}
              style={{
                background: 'transparent',
                border: 'none',
                color: active ? '#fe5a1d' : '#6b7280',
                fontWeight: active ? 700 : 500,
                fontSize: 14,
                padding: '10px 18px',
                cursor: 'pointer',
                position: 'relative',
                borderBottom: active ? '2px solid #fe5a1d' : '2px solid transparent',
                marginBottom: -1,
                transition: 'color 0.15s ease',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      <p className="qsb-label">
        {qsb.isImported ? t('quick_search.find_imported') : t('quick_search.find_perfect')}
      </p>

      <div className="qsb-fields">
        {/* Keyword */}
        <div className="qsb-field qsb-field-keyword">
          <label className="qsb-field-label">{t('quick_search.keyword')}</label>
          <Input
            className="qsb-input"
            placeholder={t('search_results.search_placeholder')}
            value={qsb.searchQuery}
            onChange={e => onFilterChange('searchQuery', e.target.value)}
            onPressEnter={onSearch}
            allowClear
          />
        </div>

        {/* Make */}
        <div className="qsb-field">
          <label className="qsb-field-label">{t('quick_search.make')}</label>
          <Select
            className="qsb-select"
            placeholder="Any Make"
            value={qsb.make || undefined}
            onChange={val => onMakeChange(val ?? '')}
            showSearch
            allowClear
            onClear={() => onMakeChange('')}
            options={groupedMakeOptions as any}
            popupClassName="qsb-dropdown"
            filterOption={(input, opt) =>
              String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            optionRender={option => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img
                  src={(option.data as any).image || DEFAULT_CAR_IMAGE}
                  alt={String(option.label)}
                  style={{ width: 20, height: 20, objectFit: 'contain', flexShrink: 0, opacity: (option.data as any).image ? 1 : 0.45 }}
                  onError={e => { e.currentTarget.src = DEFAULT_CAR_IMAGE; e.currentTarget.style.opacity = '0.45'; }}
                />
                <span>{option.label}</span>
              </div>
            )}
          />
        </div>

        {/* Model */}
        <div className="qsb-field">
          <label className="qsb-field-label">{t('quick_search.model')}</label>
          <Select
            className="qsb-select"
            placeholder={qsb.make ? 'Any Model' : 'Select make first'}
            value={qsb.model || undefined}
            onChange={val => onFilterChange('model', val ?? '')}
            showSearch
            allowClear
            onClear={() => onFilterChange('model', '')}
            disabled={!qsb.make || loadingModels}
            loading={loadingModels}
            options={modelOptions}
            popupClassName="qsb-dropdown"
            filterOption={(input, opt) =>
              (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>

        {qsb.isImported && (
          <div className="qsb-field">
            <label className="qsb-field-label">{t('quick_search.import_country')}</label>
            <Select
              className="qsb-select"
              placeholder="Any country"
              value={qsb.importCountry || undefined}
              onChange={(val) => onFilterChange('importCountry', val ?? '')}
              allowClear
              onClear={() => onFilterChange('importCountry', '')}
              options={IMPORT_COUNTRY_OPTIONS}
              showSearch
              popupClassName="qsb-dropdown"
            />
          </div>
        )}

        {/* Search button */}
        <button className="qsb-btn" onClick={onSearch}>
          <MdSearch size={20} />
          {t('quick_search.search')}
        </button>
      </div>
    </div>
  </div>
  );
};

export default QuickSearchBar;
